import createClient, { type Middleware } from 'openapi-fetch';
import type { CoreApi, CoreApiSchemas } from '@hive/contracts';
import { session } from '../auth/session';

export type Screen = CoreApiSchemas['Screen'];
export type Channel = CoreApiSchemas['Channel'];
export type Venue = CoreApiSchemas['Venue'];
export type User = CoreApiSchemas['User'];
export type ProgrammeItem = CoreApiSchemas['ProgrammeItem'];
export type Programme = CoreApiSchemas['Programme'];
export type VenueOverview = CoreApiSchemas['VenueOverview'];
export type Entitlement = CoreApiSchemas['EntitlementSummary'];
export type PatronVenue = CoreApiSchemas['PatronVenue'];
export type PatronFixture = CoreApiSchemas['PatronFixture'];
export type Area = CoreApiSchemas['Area'];
export type VenueOrder = CoreApiSchemas['VenueOrder'];
export type VenueOrders = CoreApiSchemas['VenueOrders'];
export type OrderStatus = CoreApiSchemas['OrderStatus'];
export type PatronOrderStatus = CoreApiSchemas['PatronOrderStatus'];

export const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL is not set. Add it to clients/keeper-web/.env.development or .env.local.');
}

const auth: Middleware = {
  onRequest({ request }) {
    const token = session.token();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401 && session.token()) session.clear();
    return response;
  },
};

/** Typed client generated from packages/contracts/core-api.yaml. */
export const api = createClient<CoreApi.paths>({ baseUrl });
api.use(auth);

/** Same contract, no credentials: for the public patron page. */
export const publicApi = createClient<CoreApi.paths>({ baseUrl });

/** Turns an API error body into a sentence for the UI. */
export function errorMessage(error: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string') return m;
    if (Array.isArray(m) && m.length) return String(m[0]);
  }
  return fallback;
}

/** Fetches a screen's latest preview JPEG as an object URL (images cannot send auth headers). */
export async function fetchPreview(screenId: string): Promise<string | null> {
  const token = session.token();
  const res = await fetch(`${baseUrl}/v1/screens/${encodeURIComponent(screenId)}/preview`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}
